import type { EventEffects } from '../effects/EventEffects'
import type { FlowNode, MarketFrame, VisualizationConfig } from '../types/market'
import { paletteForPolarity } from '../utils/color'
import { formatFlow, formatPercent } from '../utils/format'
import { clamp, easeInOutCubic, lerp } from '../utils/math'
import { formatTradingDate } from '../utils/marketTime'

export interface RenderScene {
  date: string
  title: string
  leftFrame: MarketFrame
  rightFrame: MarketFrame
  nodes: FlowNode[]
  progress: number
  indexPrice: number
  indexChangePercent: number
  timelineMs: number
  frameIndex: number
  zeroAxisY: number
  effects: EventEffects
}

type ChampionTone = 'inflow' | 'outflow'

export class FundsCanvasRenderer {
  private readonly context: CanvasRenderingContext2D
  private readonly rankPositions = new Map<string, number>()
  private readonly orderedNodes: FlowNode[] = []
  private readonly absoluteOrder: FlowNode[] = []
  private readonly tierById = new Map<string, number>()
  private readonly inflowNodes: FlowNode[] = []
  private readonly outflowNodes: FlowNode[] = []
  private previousTimelineMs = 0

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly config: VisualizationConfig,
    isExportMode = false,
  ) {
    const rect = canvas.getBoundingClientRect()
    const cssScale = Math.min(
      rect.width > 0 ? rect.width / config.videoWidth : 1,
      rect.height > 0 ? rect.height / config.videoHeight : 1,
    )
    // Match the canvas backing store to its actual on-screen physical size so
    // text is never upscaled and blurred. The logical 1080x1920 ceiling keeps
    // high-DPI preview monitors from accidentally rendering at 2K/4K, while
    // export mode remains exactly 1080x1920.
    const previewScale = cssScale * Math.min(window.devicePixelRatio, 2)
    const renderScale = isExportMode ? 1 : Math.min(1, Math.max(0.5, previewScale))
    canvas.width = Math.round(config.videoWidth * renderScale)
    canvas.height = Math.round(config.videoHeight * renderScale)
    const context = canvas.getContext('2d', { alpha: false })
    if (!context) throw new Error('当前浏览器无法创建 Canvas 2D 渲染上下文')
    this.context = context
    context.setTransform(renderScale, 0, 0, renderScale, 0, 0)
    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = 'high'
  }

  render(scene: RenderScene): void {
    if (scene.timelineMs < this.previousTimelineMs) this.rankPositions.clear()
    this.previousTimelineMs = scene.timelineMs
    const ctx = this.context
    const cameraScale = 1 + Math.sin(scene.timelineMs / 11_000 * Math.PI * 2) * 0.0028

    ctx.save()
    ctx.fillStyle = '#050708'
    ctx.fillRect(0, 0, this.config.videoWidth, this.config.videoHeight)
    ctx.translate(this.config.videoWidth / 2, this.config.videoHeight / 2)
    ctx.scale(cameraScale, cameraScale)
    ctx.translate(-this.config.videoWidth / 2, -this.config.videoHeight / 2)

    this.drawBackground(scene)
    this.drawAxis(scene)
    this.drawTrails(scene)
    this.drawChampionParticles(scene)
    this.drawChampionHalos(scene)
    this.drawBubbles(scene)
    this.drawChampionCrowns(scene)
    if (this.config.physicsDebug) this.drawPhysicsDebug(scene)
    this.drawAxisRipples(scene)
    this.drawAxisLabels(scene)
    this.drawHeader(scene)
    this.drawRankings(scene)
    this.drawFooter(scene)
    ctx.restore()

    this.drawVignette()
  }

  reset(): void {
    this.rankPositions.clear()
    this.previousTimelineMs = 0
  }

  private drawBackground(scene: RenderScene): void {
    const ctx = this.context
    const width = this.config.videoWidth
    const height = this.config.videoHeight
    const wash = ctx.createRadialGradient(width * 0.5, scene.zeroAxisY, 40, width * 0.5, scene.zeroAxisY, 780)
    wash.addColorStop(0, 'rgba(35, 49, 60, 0.27)')
    wash.addColorStop(0.45, 'rgba(13, 21, 29, 0.17)')
    wash.addColorStop(1, 'rgba(5, 7, 8, 0)')
    ctx.fillStyle = wash
    ctx.fillRect(0, 0, width, height)

    if (!this.config.backgroundEffects) return
    ctx.save()
    ctx.strokeStyle = 'rgba(129, 151, 165, 0.032)'
    ctx.lineWidth = 1
    for (let x = this.config.safeArea.left; x <= width - this.config.safeArea.right; x += 72) {
      ctx.beginPath()
      ctx.moveTo(x, 320)
      ctx.lineTo(x, 1550)
      ctx.stroke()
    }
    for (let y = 350; y <= 1550; y += 72) {
      ctx.beginPath()
      ctx.moveTo(this.config.safeArea.left, y)
      ctx.lineTo(width - this.config.safeArea.right, y)
      ctx.stroke()
    }

    ctx.fillStyle = 'rgba(212, 226, 233, 0.055)'
    for (let index = 0; index < 38; index += 1) {
      const x = this.config.safeArea.left + ((index * 197 + 71) % 910)
      const y = 330 + ((index * 431 + 53) % 1150)
      const shimmer = 0.35 + 0.65 * Math.sin(scene.timelineMs * 0.00042 + index * 1.91) ** 2
      ctx.globalAlpha = shimmer * 0.34
      ctx.fillRect(x, y, 1.2, 1.2)
    }
    ctx.restore()
  }

  private drawAxis(scene: RenderScene): void {
    const ctx = this.context
    const left = this.config.safeArea.left
    const right = this.config.videoWidth - this.config.safeArea.right
    const y = scene.zeroAxisY
    const axis = ctx.createLinearGradient(left, y, right, y)
    axis.addColorStop(0, 'rgba(99, 120, 134, 0)')
    axis.addColorStop(0.16, 'rgba(111, 133, 148, 0.24)')
    axis.addColorStop(0.5, 'rgba(190, 204, 212, 0.46)')
    axis.addColorStop(0.84, 'rgba(111, 133, 148, 0.24)')
    axis.addColorStop(1, 'rgba(99, 120, 134, 0)')

    ctx.save()
    ctx.shadowColor = 'rgba(159, 184, 198, 0.28)'
    ctx.shadowBlur = 12
    ctx.strokeStyle = axis
    ctx.lineWidth = 1.2
    ctx.beginPath()
    ctx.moveTo(left, y)
    ctx.lineTo(right, y)
    ctx.stroke()
    ctx.restore()

  }

  private drawAxisLabels(scene: RenderScene): void {
    const ctx = this.context
    const left = this.config.safeArea.left
    const right = this.config.videoWidth - this.config.safeArea.right
    const y = scene.zeroAxisY
    ctx.save()
    ctx.shadowColor = '#050708'
    ctx.shadowBlur = 10
    ctx.font = '500 18px "Microsoft YaHei", "PingFang SC", sans-serif'
    ctx.letterSpacing = '2px'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = 'rgba(226, 234, 239, 0.62)'
    ctx.textAlign = 'left'
    ctx.fillText('主力净流入  ↑', left + 6, y - 28)
    ctx.fillStyle = 'rgba(202, 216, 222, 0.50)'
    ctx.fillText('主力净流出  ↓', left + 6, y + 30)
    ctx.textAlign = 'right'
    ctx.font = '500 15px "Microsoft YaHei", sans-serif'
    ctx.fillStyle = 'rgba(198, 211, 220, 0.38)'
    ctx.fillText('资金零轴', right - 5, y - 22)
    ctx.textAlign = 'center'
    ctx.fillText(this.config.brandName, this.config.videoWidth / 2, y - 22)
    ctx.restore()
  }

  private drawTrails(scene: RenderScene): void {
    const ctx = this.context
    for (const node of scene.nodes) {
      const points = scene.effects.getTrail(node.id, scene.timelineMs)
      if (points.length < 2) continue
      const palette = paletteForPolarity(node.polarity)
      ctx.save()
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = palette.glow
      ctx.lineWidth = Math.max(3, node.radius * 0.07)
      ctx.globalAlpha = 0.16
      ctx.shadowColor = palette.glow
      ctx.shadowBlur = 14
      ctx.beginPath()
      points.forEach((point, index) => {
        if (index === 0) ctx.moveTo(point.x, point.y)
        else ctx.lineTo(point.x, point.y)
      })
      ctx.stroke()
      ctx.restore()
    }
  }

  private drawChampionParticles(scene: RenderScene): void {
    if (!this.config.championEffectEnabled || !this.config.championParticleEnabled) return
    const ctx = this.context
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    ctx.lineCap = 'round'
    ctx.shadowBlur = 5 * this.config.championParticleGlow
    for (const particle of scene.effects.getChampionParticles()) {
      if (!particle.active) continue
      const progress = (scene.timelineMs - particle.startedAt) / particle.duration
      if (progress < 0 || progress >= 1) continue
      const travel = Math.pow(progress, 1.35)
      const tailTravel = Math.max(0, travel - 0.055)
      const x = this.quadraticPoint(particle.startX, particle.controlX, particle.endX, travel)
      const y = this.quadraticPoint(particle.startY, particle.controlY, particle.endY, travel)
      const tailX = this.quadraticPoint(particle.startX, particle.controlX, particle.endX, tailTravel)
      const tailY = this.quadraticPoint(particle.startY, particle.controlY, particle.endY, tailTravel)
      const fadeIn = clamp(progress / 0.12, 0, 1)
      const fadeOut = clamp((1 - progress) / 0.22, 0, 1)
      const alpha = particle.brightness * fadeIn * fadeOut
      const arrivalScale = progress > 0.78 ? clamp((1 - progress) / 0.22, 0.18, 1) : 1
      const color = this.championGold(particle.colorIndex)

      ctx.strokeStyle = color
      ctx.shadowColor = color
      ctx.lineWidth = Math.max(0.8, particle.size * 0.42 * arrivalScale)
      ctx.globalAlpha = alpha * 0.42
      ctx.beginPath()
      ctx.moveTo(tailX, tailY)
      ctx.lineTo(x, y)
      ctx.stroke()

      ctx.fillStyle = color
      ctx.globalAlpha = alpha
      ctx.beginPath()
      ctx.arc(x, y, particle.size * arrivalScale, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.restore()
  }

  private drawChampionHalos(scene: RenderScene): void {
    if (!this.config.championEffectEnabled) return
    const previousId = scene.effects.getPreviousChampionId()
    const currentId = scene.effects.getCurrentChampionId()
    if (previousId) {
      this.drawChampionHalo(scene, previousId, scene.effects.championOpacity(previousId, scene.timelineMs), 'inflow')
    }
    if (currentId && currentId !== previousId) {
      this.drawChampionHalo(scene, currentId, scene.effects.championOpacity(currentId, scene.timelineMs), 'inflow')
    }
    const previousOutflowId = scene.effects.getPreviousOutflowChampionId()
    const currentOutflowId = scene.effects.getCurrentOutflowChampionId()
    if (previousOutflowId) {
      this.drawChampionHalo(
        scene,
        previousOutflowId,
        scene.effects.outflowChampionOpacity(previousOutflowId, scene.timelineMs),
        'outflow',
      )
    }
    if (currentOutflowId && currentOutflowId !== previousOutflowId) {
      this.drawChampionHalo(
        scene,
        currentOutflowId,
        scene.effects.outflowChampionOpacity(currentOutflowId, scene.timelineMs),
        'outflow',
      )
    }
  }

  private drawChampionHalo(scene: RenderScene, id: string, opacity: number, tone: ChampionTone): void {
    if (opacity <= 0.005) return
    const node = this.findNode(scene.nodes, id)
    if (!node || node.x == null || node.y == null) return
    const ctx = this.context
    const pulse = 0.5 + Math.sin(scene.timelineMs * 0.0022 + node.seed) * 0.5
    const outerPulse = 0.5 + Math.sin(scene.timelineMs * 0.0017 + node.seed * 0.73 + 1.8) * 0.5
    const isOutflow = tone === 'outflow'
    const absorb = isOutflow ? 0 : scene.effects.championAbsorbIntensity(id, scene.timelineMs)
    const intensity = this.config.championRingIntensity * opacity
    const primary = isOutflow ? '#8B68B8' : this.config.championGoldColor
    const middle = isOutflow ? '#5B3A8A' : '#F6C453'
    const outer = isOutflow ? '#3B235F' : '#E8B83E'
    const radial = isOutflow ? '#241332' : '#E8B83E'
    const innerRadius = node.radius * (1.08 + pulse * 0.07)
    const middleRadius = node.radius * (1.2 + (1 - pulse) * 0.1)
    const outerRadius = node.radius * (1.32 + outerPulse * 0.08)
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    ctx.shadowColor = primary
    ctx.shadowBlur = (18 + absorb * 10) * this.config.championParticleGlow

    ctx.globalAlpha = intensity * (0.055 + pulse * 0.025)
    ctx.strokeStyle = radial
    ctx.lineWidth = node.radius * 0.1
    ctx.beginPath()
    ctx.arc(node.x, node.y, node.radius * 1.08, 0, Math.PI * 2)
    ctx.stroke()

    ctx.globalAlpha = intensity * (0.48 + pulse * 0.12 + absorb * 0.1)
    ctx.strokeStyle = primary
    ctx.lineWidth = 2.2 + pulse * 0.8
    ctx.beginPath()
    ctx.arc(node.x, node.y, innerRadius, 0, Math.PI * 2)
    ctx.stroke()

    ctx.globalAlpha = intensity * (0.22 + (1 - pulse) * 0.09)
    ctx.strokeStyle = middle
    ctx.lineWidth = 1.4 + (1 - pulse) * 0.7
    ctx.beginPath()
    ctx.arc(node.x, node.y, middleRadius, 0, Math.PI * 2)
    ctx.stroke()

    ctx.globalAlpha = intensity * (0.075 + outerPulse * 0.045)
    ctx.strokeStyle = outer
    ctx.lineWidth = 0.9 + outerPulse * 0.5
    ctx.beginPath()
    ctx.arc(node.x, node.y, outerRadius, 0, Math.PI * 2)
    ctx.stroke()

    if (!isOutflow && absorb > 0.02) {
      const angle = scene.timelineMs * 0.0045 + node.seed
      ctx.globalAlpha = intensity * absorb * 0.45
      ctx.fillStyle = '#F6C453'
      ctx.beginPath()
      ctx.arc(
        node.x + Math.cos(angle) * node.radius * 1.06,
        node.y + Math.sin(angle) * node.radius * 1.06,
        1.6 + absorb * 1.4,
        0,
        Math.PI * 2,
      )
      ctx.fill()
    }
    ctx.restore()
  }

  private drawChampionCrowns(scene: RenderScene): void {
    if (!this.config.championEffectEnabled || !this.config.championCrownEnabled) return
    const previousId = scene.effects.getPreviousChampionId()
    const currentId = scene.effects.getCurrentChampionId()
    if (previousId) {
      this.drawChampionCrown(scene, previousId, scene.effects.championOpacity(previousId, scene.timelineMs), 'inflow')
    }
    if (currentId && currentId !== previousId) {
      this.drawChampionCrown(scene, currentId, scene.effects.championOpacity(currentId, scene.timelineMs), 'inflow')
    }
    const previousOutflowId = scene.effects.getPreviousOutflowChampionId()
    const currentOutflowId = scene.effects.getCurrentOutflowChampionId()
    if (previousOutflowId) {
      this.drawChampionCrown(
        scene,
        previousOutflowId,
        scene.effects.outflowChampionOpacity(previousOutflowId, scene.timelineMs),
        'outflow',
      )
    }
    if (currentOutflowId && currentOutflowId !== previousOutflowId) {
      this.drawChampionCrown(
        scene,
        currentOutflowId,
        scene.effects.outflowChampionOpacity(currentOutflowId, scene.timelineMs),
        'outflow',
      )
    }
  }

  private drawChampionCrown(scene: RenderScene, id: string, opacity: number, tone: ChampionTone): void {
    if (opacity <= 0.005) return
    const node = this.findNode(scene.nodes, id)
    if (!node || node.x == null || node.y == null) return
    const ctx = this.context
    const crownScale = 1.12
    const breath = 0.5 + Math.sin(scene.timelineMs * 0.002 + node.seed * 0.61) * 0.5
    const width = clamp(node.radius * 0.62, 28, 42) * crownScale
    const height = clamp(node.radius * 0.3, 15, 22) * crownScale
    const half = width * 0.5
    const baseY = node.y - node.radius - 14 - (1 - opacity) * 8
    const isOutflow = tone === 'outflow'
    const primary = isOutflow ? '#8B68B8' : this.config.championGoldColor
    const fill = isOutflow ? '#5B3A8A' : '#F6C453'
    const baseHighlight = isOutflow ? '#8B68B8' : '#F6C453'
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.shadowColor = primary
    ctx.shadowBlur = (14 + breath * 4) * this.config.championParticleGlow

    ctx.globalAlpha = opacity * (0.2 + breath * 0.05)
    ctx.fillStyle = fill
    ctx.beginPath()
    ctx.moveTo(node.x - half, baseY)
    ctx.lineTo(node.x - half * 0.76, baseY - height * 0.72)
    ctx.lineTo(node.x - half * 0.22, baseY - height * 0.3)
    ctx.lineTo(node.x, baseY - height)
    ctx.lineTo(node.x + half * 0.22, baseY - height * 0.3)
    ctx.lineTo(node.x + half * 0.76, baseY - height * 0.72)
    ctx.lineTo(node.x + half, baseY)
    ctx.closePath()
    ctx.fill()

    ctx.globalAlpha = opacity * (0.9 + breath * 0.1)
    ctx.strokeStyle = primary
    ctx.lineWidth = 2
    ctx.stroke()
    ctx.strokeStyle = baseHighlight
    ctx.lineWidth = 2.4
    ctx.beginPath()
    ctx.moveTo(node.x - half * 0.9, baseY + 3)
    ctx.quadraticCurveTo(node.x, baseY + 7, node.x + half * 0.9, baseY + 3)
    ctx.stroke()
    ctx.restore()
  }

  private drawBubbles(scene: RenderScene): void {
    this.orderedNodes.length = scene.nodes.length
    this.absoluteOrder.length = scene.nodes.length
    for (let index = 0; index < scene.nodes.length; index += 1) {
      this.orderedNodes[index] = scene.nodes[index]
      this.absoluteOrder[index] = scene.nodes[index]
    }
    this.orderedNodes.sort((a, b) => a.radius - b.radius)
    this.absoluteOrder.sort((a, b) => Math.abs(b.flow) - Math.abs(a.flow))
    this.tierById.clear()
    for (let index = 0; index < this.absoluteOrder.length; index += 1) {
      this.tierById.set(this.absoluteOrder[index].id, index)
    }

    for (const node of this.orderedNodes) {
      const x = node.x ?? 0
      const y = node.y ?? 0
      const tier = this.tierById.get(node.id) ?? 10
      const burst = scene.effects.burstIntensity(node.id, scene.timelineMs)
      const reverse = scene.effects.reversalIntensity(node.id, scene.timelineMs)
      const pulseScale = 1 + burst * 0.085 + Math.sin(scene.timelineMs * 0.0018 + node.seed) * 0.008
      const radius = node.radius * pulseScale
      if (reverse > 0.06 && Math.abs(y - scene.zeroAxisY) < 90) scene.effects.setRipplePosition(node.id, x)
      this.drawEnergySphere(node, x, y, radius, tier, burst, scene.timelineMs)
      if (tier === 0) this.drawHeroParticles(node, x, y, radius, scene.timelineMs)
    }
  }

  private drawPhysicsDebug(scene: RenderScene): void {
    const ctx = this.context
    ctx.save()
    ctx.setLineDash([7, 5])
    ctx.lineWidth = 1
    ctx.strokeStyle = 'rgba(246, 208, 92, 0.72)'
    ctx.beginPath()
    ctx.moveTo(this.config.safeArea.left, scene.zeroAxisY)
    ctx.lineTo(this.config.videoWidth - this.config.safeArea.right, scene.zeroAxisY)
    ctx.stroke()

    for (const node of scene.nodes) {
      const x = node.x ?? 0
      const y = node.y ?? 0
      ctx.strokeStyle = node.isCrossing ? 'rgba(255, 208, 82, 0.82)' : 'rgba(116, 188, 230, 0.42)'
      ctx.beginPath()
      ctx.arc(x, y, node.collisionRadius, 0, Math.PI * 2)
      ctx.stroke()
      ctx.setLineDash([3, 4])
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.lineTo(x + node.crossingLaneOffset, node.targetY)
      ctx.stroke()
      ctx.setLineDash([7, 5])
      ctx.fillStyle = 'rgba(238, 244, 247, 0.88)'
      ctx.font = '500 10px Consolas, monospace'
      ctx.textAlign = 'left'
      ctx.textBaseline = 'bottom'
      ctx.fillText(
        `${node.physicsState} p=${node.crossingProgress.toFixed(2)} v=${(node.vx ?? 0).toFixed(1)},${(node.vy ?? 0).toFixed(1)}`,
        x + 7,
        y - node.radius - 5,
      )
    }
    ctx.restore()
  }

  private drawEnergySphere(
    node: FlowNode,
    x: number,
    y: number,
    radius: number,
    tier: number,
    burst: number,
    now: number,
  ): void {
    const ctx = this.context
    const palette = paletteForPolarity(node.polarity)
    const depthOpacity = 0.76 + node.depth * 0.24
    const prominence = tier === 0 ? 1 : tier < 3 ? 0.76 : 0.48

    if (tier < 3 || burst > 0.02) {
      const aura = ctx.createRadialGradient(x, y, radius * 0.7, x, y, radius * (1.8 + burst * 0.45))
      aura.addColorStop(0, this.withAlpha(palette.glow, (0.13 + burst * 0.16) * this.config.glowIntensity))
      aura.addColorStop(0.48, this.withAlpha(palette.glow, (0.055 + burst * 0.08) * this.config.glowIntensity))
      aura.addColorStop(1, this.withAlpha(palette.glow, 0))
      ctx.fillStyle = aura
      ctx.beginPath()
      ctx.arc(x, y, radius * (1.85 + burst * 0.45), 0, Math.PI * 2)
      ctx.fill()
    }

    ctx.save()
    ctx.globalAlpha = depthOpacity
    ctx.shadowColor = palette.glow
    ctx.shadowBlur = (10 + prominence * 18 + burst * 24) * this.config.glowIntensity
    const body = ctx.createRadialGradient(
      x - radius * 0.32,
      y - radius * 0.38,
      radius * 0.06,
      x + radius * 0.08,
      y + radius * 0.1,
      radius * 1.08,
    )
    body.addColorStop(0, palette.highlight)
    body.addColorStop(0.23, palette.core)
    body.addColorStop(0.72, palette.deep)
    body.addColorStop(1, '#0b1114')
    ctx.fillStyle = body
    ctx.beginPath()
    ctx.arc(x, y, radius, 0, Math.PI * 2)
    ctx.fill()

    const internalShade = ctx.createRadialGradient(
      x + radius * 0.28,
      y + radius * 0.38,
      radius * 0.08,
      x + radius * 0.2,
      y + radius * 0.35,
      radius * 0.92,
    )
    internalShade.addColorStop(0, 'rgba(2, 5, 7, 0.22)')
    internalShade.addColorStop(0.68, 'rgba(2, 5, 7, 0.04)')
    internalShade.addColorStop(1, 'rgba(255, 255, 255, 0.06)')
    ctx.fillStyle = internalShade
    ctx.beginPath()
    ctx.arc(x, y, radius * 0.985, 0, Math.PI * 2)
    ctx.fill()

    const highlight = ctx.createRadialGradient(
      x - radius * 0.34,
      y - radius * 0.44,
      0,
      x - radius * 0.34,
      y - radius * 0.44,
      radius * 0.48,
    )
    highlight.addColorStop(0, 'rgba(255, 255, 255, 0.34)')
    highlight.addColorStop(0.28, 'rgba(255, 255, 255, 0.11)')
    highlight.addColorStop(1, 'rgba(255, 255, 255, 0)')
    ctx.fillStyle = highlight
    ctx.beginPath()
    ctx.arc(x, y, radius * 0.98, 0, Math.PI * 2)
    ctx.fill()

    ctx.lineWidth = tier < 3 ? 1.8 : 1.1
    ctx.strokeStyle = this.withAlpha(palette.highlight, tier < 3 ? 0.38 : 0.22)
    ctx.beginPath()
    ctx.arc(x, y, radius - ctx.lineWidth, 0, Math.PI * 2)
    ctx.stroke()
    ctx.restore()

    if (tier < 3 || burst > 0.08) {
      const ringProgress = (now * 0.00028 + (node.seed % 100) / 100) % 1
      const ringRadius = radius * (1.08 + ringProgress * 0.28 + burst * 0.18)
      ctx.save()
      ctx.globalAlpha = (1 - ringProgress) * (0.10 + prominence * 0.08 + burst * 0.24)
      ctx.strokeStyle = palette.glow
      ctx.lineWidth = 1.4 + burst * 1.3
      ctx.beginPath()
      ctx.arc(x, y, ringRadius, 0, Math.PI * 2)
      ctx.stroke()
      ctx.restore()
    }

    this.drawBubbleLabel(node, x, y, radius, palette.text)
  }

  private drawBubbleLabel(node: FlowNode, x: number, y: number, radius: number, textColor: string): void {
    const ctx = this.context
    const compact = radius < 52
    const nameSize = clamp(radius * 0.28, 14, 23)
    const amountSize = clamp(radius * 0.34, 16, 30)
    const name = compact && node.name.length > 5 ? `${node.name.slice(0, 4)}…` : node.name
    ctx.save()
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.shadowColor = 'rgba(0, 0, 0, 0.72)'
    ctx.shadowBlur = 7
    ctx.fillStyle = this.withAlpha(textColor, 0.88)
    ctx.font = `500 ${nameSize}px "Microsoft YaHei", "PingFang SC", sans-serif`
    ctx.fillText(name, x, y - amountSize * 0.54, radius * 1.62)
    ctx.fillStyle = textColor
    ctx.font = `650 ${amountSize}px "DIN Alternate", "Segoe UI", sans-serif`
    ctx.fillText(formatFlow(node.flow), x, y + nameSize * 0.7, radius * 1.7)
    ctx.restore()
  }

  private drawHeroParticles(node: FlowNode, x: number, y: number, radius: number, now: number): void {
    if (this.config.particleIntensity <= 0) return
    const ctx = this.context
    const palette = paletteForPolarity(node.polarity)
    ctx.save()
    ctx.fillStyle = palette.highlight
    for (let index = 0; index < 7; index += 1) {
      const phase = now * 0.00018 + index / 7 + (node.seed % 31) / 31
      const angle = phase * Math.PI * 2 * (index % 2 ? 1 : -1)
      const distance = radius * (1.26 + (index % 3) * 0.18 + Math.sin(phase * 7) * 0.07)
      ctx.globalAlpha = (0.05 + (index % 3) * 0.025) * this.config.particleIntensity
      ctx.beginPath()
      ctx.arc(x + Math.cos(angle) * distance, y + Math.sin(angle) * distance, 1.5 + (index % 2), 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.restore()
  }

  private drawAxisRipples(scene: RenderScene): void {
    const ctx = this.context
    for (const ripple of scene.effects.getRipples()) {
      if (!ripple.x) continue
      const progress = (scene.timelineMs - ripple.startedAt) / ripple.duration
      if (progress < 0 || progress > 1) continue
      const eased = easeInOutCubic(progress)
      const palette = paletteForPolarity(ripple.polarity)
      ctx.save()
      ctx.globalAlpha = Math.sin(progress * Math.PI) * 0.22
      ctx.strokeStyle = palette.glow
      ctx.lineWidth = 1.2
      ctx.beginPath()
      ctx.ellipse(ripple.x, scene.zeroAxisY, 24 + eased * 128, 6 + eased * 18, 0, 0, Math.PI * 2)
      ctx.stroke()
      ctx.restore()
    }
  }

  private drawHeader(scene: RenderScene): void {
    const ctx = this.context
    const left = this.config.safeArea.left
    const right = this.config.videoWidth - this.config.safeArea.right
    const indexPrice = scene.indexPrice
    const indexChange = scene.indexChangePercent
    const indexColor = indexChange >= 0 ? '#dc6b62' : '#38aa82'

    ctx.save()
    ctx.textBaseline = 'top'
    ctx.textAlign = 'left'
    ctx.fillStyle = 'rgba(228, 235, 239, 0.64)'
    ctx.font = '500 23px "Microsoft YaHei", sans-serif'
    ctx.letterSpacing = '2px'
    ctx.fillText(formatTradingDate(scene.date), left, 122)
    ctx.fillStyle = '#f2f5f6'
    ctx.font = '650 40px "Microsoft YaHei", sans-serif'
    ctx.letterSpacing = '1px'
    ctx.fillText(scene.title, left, 160)
    ctx.fillStyle = 'rgba(184, 199, 208, 0.43)'
    ctx.font = '500 17px "Segoe UI", sans-serif'
    ctx.letterSpacing = '3px'
    ctx.fillText('A-SHARE CAPITAL FIELD · MINUTE EVOLUTION', left, 222)

    ctx.textAlign = 'right'
    ctx.fillStyle = 'rgba(179, 195, 204, 0.52)'
    ctx.font = '500 16px "Microsoft YaHei", sans-serif'
    ctx.letterSpacing = '2px'
    ctx.fillText('CURRENT MARKET TIME', right, 116)
    this.drawRollingTime(scene.leftFrame.time, scene.rightFrame.time, scene.progress, right, 142)

    ctx.fillStyle = 'rgba(202, 214, 221, 0.69)'
    ctx.font = '500 20px "Microsoft YaHei", sans-serif'
    if (scene.leftFrame.index.available === false) {
      ctx.fillText(scene.leftFrame.index.name, right, 238)
    } else {
      ctx.fillText(`${scene.leftFrame.index.name}  ${indexPrice.toFixed(2)}`, right - 118, 238)
      ctx.fillStyle = indexColor
      ctx.font = '650 21px "DIN Alternate", "Segoe UI", sans-serif'
      ctx.fillText(formatPercent(indexChange), right, 237)
    }

    const divider = ctx.createLinearGradient(left, 0, right, 0)
    divider.addColorStop(0, 'rgba(137, 155, 166, 0.25)')
    divider.addColorStop(1, 'rgba(137, 155, 166, 0)')
    ctx.strokeStyle = divider
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(left, 288)
    ctx.lineTo(right, 288)
    ctx.stroke()

    if (scene.leftFrame.time === '11:30' && scene.rightFrame.time === '13:00') {
      const visibility = Math.sin(scene.progress * Math.PI)
      ctx.globalAlpha = visibility * 0.74
      ctx.textAlign = 'center'
      ctx.fillStyle = '#d7e0e5'
      ctx.font = '500 18px "Microsoft YaHei", sans-serif'
      ctx.letterSpacing = '5px'
      ctx.fillText('午后开盘', this.config.videoWidth / 2, 309)
    }
    ctx.restore()
  }

  private drawRollingTime(current: string, next: string, progress: number, right: number, top: number): void {
    const ctx = this.context
    const transition = clamp((progress - 0.68) / 0.32, 0, 1)
    const eased = easeInOutCubic(transition)
    ctx.save()
    ctx.beginPath()
    ctx.rect(right - 300, top, 300, 83)
    ctx.clip()
    ctx.textAlign = 'right'
    ctx.textBaseline = 'top'
    ctx.font = '700 76px "DIN Alternate", "Segoe UI", sans-serif'
    ctx.letterSpacing = '-1px'
    ctx.fillStyle = '#f3f6f7'
    ctx.globalAlpha = 1 - eased
    ctx.fillText(current, right, top - eased * 65)
    if (current !== next) {
      ctx.globalAlpha = eased
      ctx.fillText(next, right, top + 65 - eased * 65)
    }
    ctx.restore()
  }

  private drawRankings(scene: RenderScene): void {
    this.inflowNodes.length = 0
    this.outflowNodes.length = 0
    for (const node of scene.nodes) {
      if (node.flow > 0) this.inflowNodes.push(node)
      else if (node.flow < 0) this.outflowNodes.push(node)
    }
    this.inflowNodes.sort((a, b) => b.flow - a.flow)
    this.outflowNodes.sort((a, b) => a.flow - b.flow)
    if (this.inflowNodes.length > this.config.topCount) this.inflowNodes.length = this.config.topCount
    if (this.outflowNodes.length > this.config.topCount) this.outflowNodes.length = this.config.topCount
    this.drawRankingPanel('in', '净流入 TOP 5', this.inflowNodes, 60, 1550, 462, '#d76860')
    this.drawRankingPanel('out', '净流出 TOP 5', this.outflowNodes, 550, 1550, 444, '#35a67f')
  }

  private drawRankingPanel(
    key: string,
    title: string,
    nodes: FlowNode[],
    x: number,
    y: number,
    width: number,
    color: string,
  ): void {
    const ctx = this.context
    const height = 210
    ctx.save()
    const panel = ctx.createLinearGradient(x, y, x + width, y + height)
    panel.addColorStop(0, 'rgba(19, 27, 33, 0.80)')
    panel.addColorStop(1, 'rgba(9, 14, 18, 0.54)')
    ctx.fillStyle = panel
    ctx.strokeStyle = 'rgba(153, 172, 183, 0.11)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.roundRect(x, y, width, height, 22)
    ctx.fill()
    ctx.stroke()

    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = color
    ctx.font = '650 17px "Microsoft YaHei", sans-serif'
    ctx.letterSpacing = '2px'
    ctx.fillText(title, x + 22, y + 29)
    ctx.fillStyle = 'rgba(180, 196, 205, 0.32)'
    ctx.font = '500 12px "Segoe UI", sans-serif'
    ctx.textAlign = 'right'
    ctx.fillText('ABS CAPITAL', x + width - 20, y + 29)

    for (let targetRank = 0; targetRank < nodes.length; targetRank += 1) {
      const node = nodes[targetRank]
      const mapKey = `${key}:${node.id}`
      const current = this.rankPositions.get(mapKey) ?? Math.min(targetRank + 0.85, this.config.topCount - 0.1)
      const visualRank = lerp(current, targetRank, 0.16)
      this.rankPositions.set(mapKey, visualRank)
      const rowY = y + 68 + visualRank * 32
      const rank = targetRank + 1
      ctx.textBaseline = 'middle'
      ctx.textAlign = 'left'
      ctx.fillStyle = rank === 1 ? color : 'rgba(178, 194, 203, 0.47)'
      ctx.font = `650 ${rank === 1 ? 17 : 15}px "DIN Alternate", sans-serif`
      ctx.fillText(String(rank).padStart(2, '0'), x + 22, rowY)
      ctx.fillStyle = rank === 1 ? '#eef3f5' : 'rgba(221, 229, 233, 0.75)'
      ctx.font = `500 ${rank === 1 ? 18 : 16}px "Microsoft YaHei", sans-serif`
      ctx.fillText(node.name, x + 66, rowY)
      ctx.textAlign = 'right'
      ctx.fillStyle = color
      ctx.font = `650 ${rank === 1 ? 18 : 16}px "DIN Alternate", sans-serif`
      ctx.fillText(formatFlow(node.flow), x + width - 20, rowY)
    }
    ctx.restore()
  }

  private drawFooter(scene: RenderScene): void {
    const ctx = this.context
    const left = this.config.safeArea.left
    const right = this.config.videoWidth - this.config.safeArea.right
    ctx.save()
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'left'
    ctx.fillStyle = 'rgba(230, 236, 239, 0.74)'
    ctx.font = '650 20px "Microsoft YaHei", sans-serif'
    ctx.fillText(this.config.brandName, left, 1820)
    ctx.fillStyle = 'rgba(174, 190, 199, 0.35)'
    ctx.font = '500 13px "Segoe UI", sans-serif'
    ctx.letterSpacing = '2px'
    ctx.textAlign = 'right'
    ctx.fillStyle = 'rgba(180, 194, 201, 0.42)'
    ctx.font = '500 14px "Microsoft YaHei", sans-serif'
    ctx.letterSpacing = '0px'
    ctx.fillText('数据仅供参考，不构成任何投资建议。市场有风险，投资需谨慎。', right, 1820)
    ctx.restore()
  }

  private drawVignette(): void {
    const ctx = this.context
    const width = this.config.videoWidth
    const height = this.config.videoHeight
    const vignette = ctx.createRadialGradient(width / 2, height * 0.48, 420, width / 2, height * 0.5, 1120)
    vignette.addColorStop(0, 'rgba(0, 0, 0, 0)')
    vignette.addColorStop(0.72, 'rgba(0, 0, 0, 0.07)')
    vignette.addColorStop(1, 'rgba(0, 0, 0, 0.44)')
    ctx.fillStyle = vignette
    ctx.fillRect(0, 0, width, height)
  }

  private findNode(nodes: FlowNode[], id: string): FlowNode | undefined {
    for (const node of nodes) {
      if (node.id === id) return node
    }
    return undefined
  }

  private quadraticPoint(start: number, control: number, end: number, progress: number): number {
    const inverse = 1 - progress
    return inverse * inverse * start + 2 * inverse * progress * control + progress * progress * end
  }

  private championGold(index: number): string {
    if (index === 1) return '#F6C453'
    if (index === 2) return '#E8B83E'
    return this.config.championGoldColor
  }

  private withAlpha(color: string, alpha: number): string {
    if (color.startsWith('rgb(')) return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`)
    const normalized = color.replace('#', '')
    const red = Number.parseInt(normalized.slice(0, 2), 16)
    const green = Number.parseInt(normalized.slice(2, 4), 16)
    const blue = Number.parseInt(normalized.slice(4, 6), 16)
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`
  }
}
